import { useContext, useState } from "react";

import { Logger } from "@liberty-fund/logger";
import { getAccessToken } from "@liberty-fund/utils/auth";
import { HostServiceContext } from "@liberty-fund/utils/hostServices";

/**
 * How long the object URL is held after the new tab is pointed at it. The tab
 * reads the blob asynchronously, so revoking promptly can leave it blank — and
 * these are conference readers, big enough to have OOM'd the container that
 * used to stream them. Held well past any plausible load.
 */
const REVOKE_DELAY_MS = 60_000;

/**
 * Shown in the new tab while the asset is being fetched. The tab has to be
 * opened synchronously (see below), which is necessarily before there is
 * anything to show in it.
 *
 * ASCII "..." and an explicit charset: a Unicode ellipsis in a charset-less
 * blob is what rendered as "Opening documentâ€¦". Colors match the portal
 * (`gray-50` / `lf-blue-dark` / `lf-blue-light-2`).
 */
const PLACEHOLDER_HTML = `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8">
    <title>Opening document...</title>
    <style>
      html, body {
        height: 100%;
        margin: 0;
        background: #f9fafb;
        color: #01315A;
        font-family: ui-sans-serif, system-ui, sans-serif;
      }
      .frame {
        min-height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 1rem;
        padding: 2rem;
        text-align: center;
      }
      h1 {
        margin: 0;
        font-size: 1.25rem;
        font-weight: 600;
        line-height: 1.5;
      }
      .row {
        display: flex;
        align-items: center;
        gap: 0.75rem;
      }
      p {
        margin: 0;
        font-size: 1rem;
        font-style: italic;
        color: #13415E;
      }
      .loader {
        width: 32px;
        height: 32px;
        border: 5px solid #BCDBF6;
        border-bottom-color: #01315A;
        border-radius: 50%;
        box-sizing: border-box;
        animation: rotation 1s linear infinite;
      }
      @keyframes rotation {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @media (prefers-reduced-motion: reduce) {
        .loader { animation: none; border-color: #01315A; }
      }
    </style>
  </head>
  <body>
    <div class="frame">
      <h1>Opening document</h1>
      <div class="row">
        <p>Please wait...</p>
        <div class="loader" aria-hidden="true"></div>
      </div>
    </div>
  </body>
</html>`;

interface AssetLinkProps extends Omit<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  "href" | "onClick" | "onAuxClick" | "download"
> {
  /** Authenticated asset URL, as returned by `getAssetUrl`. */
  href: string;
  children: React.ReactNode;
  /** Called once the open attempt settles. Optional; used for analytics. */
  onOpen?: (status: "success" | "error") => void;
}

/**
 * Opens an authenticated asset in a new tab.
 *
 * The assets service requires a bearer token, and a browser cannot attach one
 * to a plain `<a href>` any more than it can to an `<img src>` — following the
 * link just yields a 401 at the CloudFront edge. Under the Node container this
 * worked because the URL pointed at `/api/util/fetch-asset`, which stamped the
 * header server-side and streamed the bytes back same-origin. That route went
 * away with the container, so the click is intercepted, the asset fetched with
 * a token, and the resulting blob shown in a tab.
 *
 * The tab is opened *synchronously*, before the fetch: a `window.open` issued
 * after an `await` has lost its user-gesture provenance and is what popup
 * blockers exist to stop. So an empty tab is claimed on the click and pointed
 * at the blob once it arrives — the same approach `ConfereeDetailsForms` uses
 * for tax-form PDFs.
 */
const AssetLink = ({
  href,
  children,
  className,
  onOpen,
  ...rest
}: AssetLinkProps) => {
  const [state, setState] = useState<"idle" | "loading" | "error">("idle");
  // Read from context directly rather than through `useHostServices`, which
  // throws when there is no provider — this component should still render in
  // isolation, just without the toast.
  const addToast = useContext(HostServiceContext)?.addToast;

  /**
   * Every way of activating the link is intercepted — plain click, modified
   * click, and middle click via `auxclick`. Letting any of them through would
   * request the bare asset URL without a token and open a 401 error page, so
   * they all route through the fetch instead; the outcome (a new tab) is what
   * a middle click was asking for anyway.
   */
  const openAsset = async (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    if (state === "loading") return;

    setState("loading");
    let objectUrl: string | undefined;

    // Claimed before any await, while the click is still the reason this code
    // is running. `null` means the popup blocker took it anyway.
    //
    // Do not pass `noopener` (or `noreferrer`, which implies it) as a window
    // feature: the HTML spec then returns null even when the tab opened, which
    // is the WindowProxy we need to retarget at the blob. Sever `opener`
    // ourselves instead, matching ConfereeDetailsForms.
    const placeholderUrl = URL.createObjectURL(
      new Blob([PLACEHOLDER_HTML], { type: "text/html;charset=utf-8" }),
    );
    const assetWindow = window.open(placeholderUrl, "_blank");
    if (assetWindow) assetWindow.opener = null;
    URL.revokeObjectURL(placeholderUrl);

    try {
      const accessToken = await getAccessToken();
      const response = await fetch(href, {
        headers: accessToken
          ? { Authorization: `Bearer ${accessToken}` }
          : undefined,
      });

      if (!response.ok) {
        throw new Error(`Asset request failed with ${response.status}`);
      }

      const blob = await response.blob();
      objectUrl = URL.createObjectURL(blob);

      if (assetWindow && !assetWindow.closed) {
        assetWindow.location.href = objectUrl;
      } else {
        // The placeholder was blocked or the user closed it mid-fetch. This
        // open may itself be blocked, which is why it isn't the primary path.
        window.open(objectUrl, "_blank", "noopener,noreferrer");
      }

      setState("idle");
      onOpen?.("success");
    } catch (error) {
      // Don't strand a blank tab explaining nothing; the toast carries the
      // failure instead.
      assetWindow?.close();

      Logger.error("AssetLink failed to open asset", {
        href,
        message: (error as Error)?.message,
      });
      setState("error");
      onOpen?.("error");
      // Without this the click is indistinguishable from one that did nothing:
      // the busy cursor clears and the page is unchanged. The most likely cause
      // in practice is the assets domain not sending CORS headers for this
      // origin, which the user cannot diagnose from an unresponsive icon.
      addToast?.(
        "Could not open document",
        "The document could not be opened. Please try again.",
        "error",
      );
    } finally {
      if (objectUrl) {
        const url = objectUrl;
        setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
      }
    }
  };

  return (
    <a
      {...rest}
      href={href}
      onClick={openAsset}
      // Middle click fires auxclick, not click, and would otherwise open the
      // bare URL in a new tab and 401.
      onAuxClick={openAsset}
      aria-busy={state === "loading"}
      title={state === "error" ? "Could not open document" : rest.title}
      className={[className, state === "loading" ? "cursor-wait" : undefined]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </a>
  );
};

AssetLink.displayName = "AssetLink";

export default AssetLink;
export type { AssetLinkProps };
